import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import { orderBy } from '@progress/kendo-data-query';
import { TFunction } from 'i18next';
import createGridCustomCells from '@components-clean/core/grid/custom-cell';
import { GridHeaderTitle, RowMenuAction } from '@components-clean/core/grid/grid.interface';
import Grid from '@components-clean/core/grid-fe';
import Loading from '@components-clean/core/loading/loading';
import {
    GeneratedReport,
    GeneratedReportStatus,
    OpraReportTemplate,
    ReportDownload,
} from '@interfaces/report-templates.interface';
import { ReportStatusUpdatedFields } from '@interfaces-clean/reports.interface';
import { EditReportDialog } from '@pages-clean/shared/generated-reports/edit-report-dialog/edit-report-dialog';
import { GENERATED_REPORTS_GRID_COLUMNS } from '@pages-clean/shared/generated-reports/grid-columns';
import { getGeneratedReportsGridData } from '@pages-clean/shared/generated-reports/helper';
import GenerateReportDialog from '@pages-clean/shared/reports/generate-report-dialog';
import { CommonDataset } from '@pages-clean/shared/types';
import { mapApplicationArea, mapService } from '@utils-clean/grid-download-audit-helper';
import { AuditDataType, AuditServiceType } from 'types/graphql-global-types';
import { IUser, Permissions } from '@contexts/user-context';
import { useLocalState, useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import { stringDateWithoutTimeFromCurrentUtcDate } from '@utils/convertor';
import { downloadBase64File } from '@utils/file-service';
import NotificationService from '@utils/notification';
import { addPrefixedRecordIdsToTemplates } from '@utils/prefixes-helper';
import { connectToReportingServer, reportingConnection } from '@utils/reporting-signalr-service';
import { isReportStatusUpdated } from '@utils/shared';
import { AUDIT_RECORD_ID_PREFIXES } from '@queries/audit';
import { GET_DATASETS } from '@queries/central-monitoring';
import { auditRecordIdPrefixes } from '@queries/generated/audit.generated';
import { dataSets, dataSetsVariables } from '@queries/generated/central-monitoring.generated';
import DatasetApi from '@apis/bff-api/central-monitoring/dataset';
import { useGetGeneratedReports, useMutationDownloadReportKeyArray } from '@apis/bff-api/report-templates';

type Props = {
    orgKey: string;
    module: number;
    currentTemplate?: OpraReportTemplate;
    isGenerationHistory?: boolean;
    studyId: string;
    user?: IUser;
};

enum ReportGridView {
    MyReports = 1,
    AllReports = 2,
}

const Chips = (t: TFunction): GridHeaderTitle[] => {
    return [
        {
            id: ReportGridView.MyReports,
            label: t('cm:myReports'),
        },
        {
            id: ReportGridView.AllReports,
            label: t('cm:allReports'),
        },
    ];
};

// TODO: Add Generate Report button when not generation history
// TODO: Support Add Dialog

export const GeneratedReportsGrid = ({
    orgKey,
    module,
    currentTemplate,
    isGenerationHistory,
    studyId,
    user,
}: Props) => {
    const { t } = useTranslation();
    const localState = useLocalState();
    const customCells = createGridCustomCells();

    const generationTitle: GridHeaderTitle[] = Chips(t);
    const [selectedChip, setSelectedChip] = useState<GridHeaderTitle | undefined>(generationTitle[0]);

    const [allGeneratedReports, setAllGeneratedReports] = useState<GeneratedReport[]>([]);
    const [historyGeneratedReports, setHistoryGeneratedReports] = useState<GeneratedReport[]>([]);
    const currentReportRef = useRef<GeneratedReport | null>(null);

    const [datasets, setDatasets] = useState<CommonDataset[]>([]);

    const [showEditReportDialog, setShowEditReportDialog] = useState<boolean>(false);
    const [selectedReport, setSelectedReport] = useState<GeneratedReport | null>(null);

    const { data: cm2Datasets, isLoading: cm2DatasetsLoading } = DatasetApi.useGetAll(
        studyId,
        module !== Constants.Modules.CM2 && module !== Constants.Modules.SM, // THis check might not be right for RAM, need to look at study?
    );
    const [loadLegacyDatasets, { data: legacyDatasets, loading: legacyDatasetsLoading }] = useLazyQuery<
        dataSets,
        dataSetsVariables
    >(GET_DATASETS);
    const loadingDatasets = cm2DatasetsLoading || legacyDatasetsLoading;

    useEffect(() => {
        if (module !== Constants.Modules.CM2 && module !== Constants.Modules.SM)
            loadLegacyDatasets({
                variables: { studyId },
            }).catch((err) => window.console.error(err));
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    useEffect(() => {
        if (loadingDatasets) return;

        const d: CommonDataset[] = [];
        if (legacyDatasets?.dataSets?.length)
            legacyDatasets.dataSets.forEach((dataSet) => {
                if (dataSet) {
                    d.push({
                        id: dataSet.id as string,
                        dataCutDate: dataSet.dataCutDate as string,
                        active: dataSet.active || false,
                    });
                }
            });

        if (cm2Datasets)
            cm2Datasets.forEach((dataSet) => {
                d.push({
                    id: dataSet.id!.toString(),
                    dataCutDate: dataSet.dataCutDate,
                    active: dataSet.active || false,
                });
            });

        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setDatasets(d);
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [cm2DatasetsLoading, legacyDatasetsLoading]);

    const {
        data: generatedReportsData,
        mutate,
        isLoading,
        isValidating,
    } = useGetGeneratedReports(orgKey, studyId, module, currentTemplate?.id);

    const { data: prefixesData, loading: isLoadingPrefixes } = useQuery<auditRecordIdPrefixes>(
        AUDIT_RECORD_ID_PREFIXES,
        { fetchPolicy: 'cache-first' },
    );
    const { trigger: downloadGeneratedReport } = useMutationDownloadReportKeyArray(() => {
        if (!currentReportRef.current?.id) return null;
        return [currentReportRef.current.id, studyId, module];
    });
    const loadingData = loadingDatasets || isLoading || isLoadingPrefixes;

    function filterHistoricGeneratedReports(reportData: GeneratedReport[], gridView: ReportGridView) {
        const reports =
            gridView === ReportGridView.MyReports ? reportData.filter((item) => item.userId === user?.id) : reportData;
        const reportsWithPrefixedIRecordIds = addPrefixedRecordIdsToTemplates(
            reports,
            AuditServiceType.REPORTING,
            prefixesData?.auditRecordIdPrefixes,
        );
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setHistoryGeneratedReports(reportsWithPrefixedIRecordIds);
    }

    const reportMessageForUserHandler = (message: string) => {
        NotificationService.showNotification(t(`reports:${message}`));
    };

    const generationUpdateForUserHandler = (data: ReportStatusUpdatedFields) => {
        if (data && isReportStatusUpdated(allGeneratedReports, data)) {
            mutate().catch((e) => window.console.error(e));
        }
    };

    const isCM = module === Constants.Modules.CM2;
    const isSM = module === Constants.Modules.SM;
    const canEdit = !!user?.canWrite?.({
        module,
        element: isCM
            ? Constants.DataElements.CentralMonitoringDefault
            : isSM
              ? Constants.DataElements.SubjectMonitoring
              : Constants.DataElements.RiskAssessment,
        permission: Permissions.ReadWrite,
    });
    const canRead = !!user?.canRead?.({
        module,
        element: isCM
            ? Constants.DataElements.CentralMonitoringDefault
            : isSM
              ? Constants.DataElements.SubjectMonitoring
              : Constants.DataElements.RiskAssessment,
        permission: Permissions.Read,
    });

    useEffect(() => {
        user && connectToReportingServer(user);

        reportingConnection.on('ReportMessageForUser', reportMessageForUserHandler);
        reportingConnection.on('GenerationUpdateForUser', generationUpdateForUserHandler);

        return () => {
            reportingConnection.off('ReportMessageForUser', reportMessageForUserHandler);
            reportingConnection.off('GenerationUpdateForUser', generationUpdateForUserHandler);
        };
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [allGeneratedReports]); // A dependent in generationUpdateForUserHandler

    useEffect(() => {
        if (loadingData || loadingDatasets) return;
        if (isGenerationHistory || currentTemplate) {
            const reportData = orderBy(
                getGeneratedReportsGridData(generatedReportsData, datasets, prefixesData?.auditRecordIdPrefixes),
                [{ field: 'createdDate', dir: 'desc' }],
            );
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setAllGeneratedReports(reportData);

            if (!isGenerationHistory) return;
            filterHistoricGeneratedReports(reportData, selectedChip?.id as ReportGridView);
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [generatedReportsData, datasets, loadingData, loadingDatasets]);

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setSelectedReport(selectedReport);
    }, [selectedReport]);

    const setSelectedRow = async (success: boolean | undefined, id: string | undefined) => {
        if (success) {
            const mutatedList = await mutate();

            if (id && mutatedList) {
                const report = mutatedList?.find((s) => s.id === id);
                setSelectedReport(report ?? null);
            }
        }
    };

    const columns = useMemo(
        () =>
            GENERATED_REPORTS_GRID_COLUMNS(
                t,
                customCells,
                module,
                isGenerationHistory,
                currentTemplate?.includeNameAndVersion,
                currentTemplate?.includeComparisonDataset,
            ),
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
        [
            module,
            isGenerationHistory,
            currentTemplate?.includeNameAndVersion,
            currentTemplate?.includeComparisonDataset,
        ],
    );

    const onDownloadReport = () => {
        downloadGeneratedReport()
            .then((data: ReportDownload[]) => {
                const fileData = data?.[0];
                if (!fileData) {
                    NotificationService.showNotification(t('reports:reportInNotAvailable'));
                    return;
                }

                downloadBase64File(`${fileData.filename}.${fileData.fileType}`, fileData.contentBase64 ?? '');
            })
            .catch(() => {
                NotificationService.showNotification(t('errorMessage'));
            })
            .finally(() => {
                currentReportRef.current = null;
            });
    };

    const rowMenuActions: RowMenuAction[] = [];
    if (!isGenerationHistory)
        rowMenuActions.push({
            id: 1,
            label: t('reports:editReport'),
            onClick: (dataItem: unknown) => {
                currentReportRef.current = dataItem as GeneratedReport;
                setShowEditReportDialog(true);
            },
            icon: ['far', 'pen-to-square'],
            rowDisabled: (dataItem: unknown) => {
                return (
                    !canEdit ||
                    [GeneratedReportStatus.Failed, GeneratedReportStatus.Queued].includes(
                        (dataItem as GeneratedReport).status!,
                    )
                );
            },
        });

    rowMenuActions.push({
        id: 2,
        label: t('reports:downloadReport'),
        onClick: (dataItem: unknown) => {
            currentReportRef.current = dataItem as GeneratedReport;
            onDownloadReport();
        },
        icon: ['far', 'download'],
        rowDisabled: (dataItem: unknown) => {
            return (
                !canRead ||
                [GeneratedReportStatus.Failed, GeneratedReportStatus.Queued].includes(
                    (dataItem as GeneratedReport).status!,
                )
            );
        },
    });

    const onChipChange = (titleId: ReportGridView) => {
        const selected = generationTitle.find((x) => x.id === titleId);
        setSelectedChip(selected);

        filterHistoricGeneratedReports(allGeneratedReports, titleId);
    };

    const studyName = localState.state?.selectedStudy?.studyNumber;

    const exportFileName = () => {
        const now = stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-YYYY');
        if (currentTemplate)
            return `Generated Reports for Template: ${currentTemplate?.templateName}_${studyName}_${now}`;

        return `Generation History - ${selectedChip?.label as string}_${studyName}_${now}`;
    };

    if (loadingData) return <Loading />;

    return (
        <>
            <Grid
                // key forces a re-mount when template changes, to reset column visibility
                key={`${currentTemplate?.id ?? 'none'}`}
                maxHeight="auto"
                testDataId="generated-reports-grid"
                data={isGenerationHistory ? historyGeneratedReports : allGeneratedReports}
                showPlaceholder={false}
                sortable
                filterable
                selectable={{
                    enabled: true,
                    mode: 'single',
                }}
                columns={columns}
                title={isGenerationHistory ? generationTitle : t('cm:generatedReports')}
                onChipHeaderSelected={onChipChange}
                selectedChipHeader={selectedChip?.id as ReportGridView}
                selectedTitle={selectedChip}
                exportFilename={exportFileName()}
                service={mapService(module)}
                area={mapApplicationArea(module)}
                dataType={AuditDataType.EXCEL_EXPORT}
                showAddIcon={!isGenerationHistory && currentTemplate?.inUse}
                rowMenuActions={rowMenuActions}
                loading={isValidating}
                canEdit={canEdit}
                defaultSelectedItem={selectedReport ?? undefined}
                dataItemKey="id"
                addRecordTooltip={t('reports:generateReport')}
                addItemDialog={(onSuccessClear, onCancel) => (
                    <GenerateReportDialog
                        onClose={onCancel}
                        onSubmit={async (success, id) => {
                            onSuccessClear(true, true); // clear filters and sort
                            await setSelectedRow(success, id);
                            onCancel();
                        }}
                        dataSets={datasets}
                        includeNameAndVersion={currentTemplate?.includeNameAndVersion}
                        includeComparisonDataset={currentTemplate?.includeComparisonDataset}
                        includeSignatories={currentTemplate?.includeSignatures}
                        includeRiskReviews={currentTemplate?.includeReviews}
                        latestReport={allGeneratedReports[0]}
                        studyId={studyId}
                        module={module}
                        currentTemplateId={currentTemplate?.id}
                        orgKey={orgKey}
                    />
                )}
            />
            {showEditReportDialog && currentReportRef.current && (
                <EditReportDialog
                    report={currentReportRef.current}
                    onClose={(success) => {
                        setShowEditReportDialog(false);
                        currentReportRef.current = null;

                        if (success) {
                            NotificationService.showNotification(t('actionsSuccessfulToast'));
                            mutate().catch((e) => window.console.error(e));
                        }
                    }}
                />
            )}
        </>
    );
};
